/**
 * Averages & Returns Component
 * Displays average win, average loss, daily gain/loss, per-share gain/loss, and daily volume
 */

import React, { useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { StatisticsCardHeadline } from "./statisticsCardHeadline";
import { STATISTICS_NO_DATA_TIMEFRAME_MESSAGE } from "./statisticsChartTooltip";
import { DataTable } from "../../../../components/ui/data-table";

interface AveragesReturnsTableProps {
  statistics: {
    averageWin: number;
    averageLoss: number;
  };
  backendStats?: {
    average_daily_gain_loss?: number | null;
    average_per_unit_pnl?: number | null;
    average_daily_volume?: number | null;
  } | null;
}

interface AverageRow {
  metric: string;
  value: string;
}

const AveragesReturnsTable: React.FC<AveragesReturnsTableProps> = ({
  statistics,
  backendStats,
}) => {
  const { formatMoney } = useDisplayCurrency();

  const tableData: AverageRow[] = useMemo(() => [
    {
      metric: "Average Win",
      value: formatMoney(statistics.averageWin),
    },
    {
      metric: "Average Loss",
      value: formatMoney(statistics.averageLoss),
    },
    {
      metric: "Avg Daily Gain/Loss",
      value: backendStats?.average_daily_gain_loss != null
        ? formatMoney(backendStats.average_daily_gain_loss)
        : "N/A",
    },
    {
      metric: "Avg Per-share Gain/Loss",
      value: backendStats?.average_per_unit_pnl != null
        ? formatMoney(backendStats.average_per_unit_pnl)
        : "N/A",
    },
    {
      metric: "Avg Daily Volume",
      value: backendStats?.average_daily_volume != null
        ? backendStats.average_daily_volume.toFixed(2)
        : "N/A",
    },
  ], [statistics, backendStats, formatMoney]);

  const columns: ColumnDef<AverageRow, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "metric",
        header: "Metric",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
            {row.getValue("metric")}
          </span>
        ),
      },
      {
        accessorKey: "value",
        header: "Value",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="font-[family-name:var(--font-primary)] text-sm font-medium text-gray-500">
            {row.getValue("value")}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div data-statistics-shadcn-card className="rounded-[6px] border border-border bg-background p-4">
      <div className="mb-4">
        <StatisticsCardHeadline>Averages & Returns</StatisticsCardHeadline>
      </div>
      <DataTable
        columns={columns}
        data={tableData}
        showPagination={false}
        emptyMessage={STATISTICS_NO_DATA_TIMEFRAME_MESSAGE}
      />
    </div>
  );
};

export default AveragesReturnsTable;
